/**
 * Tâche 1423 (Eric 2026-10-01) — Carte de l'ouvrier du pipeline.
 *
 * Deux choses en un seul endroit, et c'est voulu : l'ÉTAT de l'ouvrier et le
 * RÉGLAGE de sa concurrence. On règle en voyant l'effet, comme le seuil de
 * divergence de 1397 est posé sous la légende qu'il pilote.
 *
 * `worker_alive` est la seule information qui permette de répondre à
 * « pourquoi rien ne se traite ? » sans aller lire un journal : une file qui
 * n'avance pas parce qu'il n'y a rien à faire et une file qui n'avance pas
 * parce que l'ouvrier est mort se ressemblent à l'écran, et elles n'appellent
 * pas du tout la même réaction.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Cpu, Warning, CheckCircle } from "@phosphor-icons/react";

import { API_BASE as API } from "../../lib/apiBase";
import { useLang } from "../../contexts/LanguageContext";

export default function PipelineWorkerCard({ isFounder }) {
  const { lang, tr } = useLang();
  const [etat, setEtat] = useState(null);
  // La valeur EN COURS DE SAISIE est tenue à part de la valeur appliquée
  // (acquis 1397) : sans cela, la carte se recalculerait à chaque frappe.
  const [saisie, setSaisie] = useState("");
  const [enregistre, setEnregistre] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/videos/queue/worker-settings`, { withCredentials: true });
      setEtat(data);
      setSaisie((prec) => (prec === "" ? String(data.max_concurrent) : prec));
    } catch {
      setEtat(null);
    }
  }, []);

  useEffect(() => {
    charger();
    const t = setInterval(charger, 10000);
    return () => clearInterval(t);
  }, [charger]);

  const enregistrer = async () => {
    const valeur = parseInt(saisie, 10);
    if (!Number.isFinite(valeur)) {
      toast.error(tr("Entre un nombre entier.", "Enter a whole number."));
      return;
    }
    setEnregistre(true);
    try {
      const { data } = await axios.put(
        `${API}/api/videos/queue/worker-settings`,
        { max_concurrent: valeur },
        { withCredentials: true },
      );
      toast.success(tr(
        `${data.max_concurrent} traitement(s) en parallèle — appliqué dans quelques secondes.`,
        `${data.max_concurrent} parallel job(s) — applied within seconds.`,
      ));
      await charger();
    } catch (err) {
      toast.error(err?.response?.data?.detail
        || tr("Enregistrement impossible.", "Could not save."));
    } finally {
      setEnregistre(false);
    }
  };

  if (!etat) return null;

  const modifie = String(etat.max_concurrent) !== String(saisie).trim();

  return (
    <div
      className="rounded-xl border border-zinc-200 bg-white p-4"
      data-testid="pipeline-worker-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <Cpu size={18} weight="bold" className="text-[#002FA7] shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-zinc-900">
              {tr("Ouvrier du pipeline", "Pipeline worker")}
            </p>
            <p className="text-[11px] text-zinc-500 leading-snug">
              {tr(
                "Les traitements média tournent dans un processus distinct : l'interface n'est plus ralentie par eux.",
                "Media processing runs in a separate process: the interface is no longer slowed down by it.",
              )}
            </p>
          </div>
        </div>
        {etat.worker_alive ? (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 whitespace-nowrap"
            data-testid="pipeline-worker-alive"
          >
            <CheckCircle size={12} weight="bold" />
            {tr("Ouvrier actif", "Worker running")}
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-800 whitespace-nowrap"
            data-testid="pipeline-worker-dead"
            title={etat.worker_last_seen
              ? (lang === "fr" ? `Dernier signe de vie : ${etat.worker_last_seen}` : `Last seen: ${etat.worker_last_seen}`)
              : (lang === "fr" ? "Aucun signe de vie depuis le démarrage" : "No sign of life since startup")}
          >
            <Warning size={12} weight="bold" />
            {tr("Ouvrier arrêté", "Worker stopped")}
          </span>
        )}
      </div>

      {/* Tâche 1427 — « Ouvrier arrêté » sans suite à donner laisse l'écran
          sur un constat. On dit donc ce qui se passe TOUT SEUL (relance par le
          poller en moins de 30 s) et ce que cela implique pour la file : rien
          n'est perdu, les envois attendent. */}
      {!etat.worker_alive && (
        <p className="mt-2 text-[11px] text-red-800" data-testid="pipeline-worker-dead-hint">
          {tr(
            "Relance automatique dans les 30 secondes. Les envois restent en file — rien n'est perdu. Si l'état persiste, le journal de l'ouvrier en donne la cause.",
            "Automatic restart within 30 seconds. Uploads stay queued — nothing is lost. If this persists, the worker log gives the reason.",
          )}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-end gap-3 border-t border-zinc-100 pt-3">
        <div>
          <label
            className="block text-[11px] font-semibold uppercase tracking-wide text-zinc-500"
            htmlFor="pipeline-max-concurrent"
          >
            {tr("Traitements en parallèle", "Parallel jobs")}
          </label>
          <input
            id="pipeline-max-concurrent"
            type="number"
            min={etat.min_allowed}
            max={etat.max_allowed}
            value={saisie}
            disabled={!isFounder}
            onChange={(e) => setSaisie(e.target.value)}
            className="mt-1 w-24 rounded-lg border border-zinc-300 px-2.5 py-1.5 text-sm tabular-nums disabled:bg-zinc-100 disabled:text-zinc-500"
            data-testid="pipeline-max-concurrent-input"
          />
        </div>
        {isFounder && (
          <button
            type="button"
            onClick={enregistrer}
            disabled={!modifie || enregistre}
            className="rounded-lg bg-[#002FA7] px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#002687] disabled:cursor-not-allowed disabled:bg-zinc-300"
            data-testid="pipeline-max-concurrent-save"
          >
            {enregistre ? tr("Enregistrement…", "Saving…") : tr("Appliquer", "Apply")}
          </button>
        )}
        <div className="flex-1 min-w-[220px] text-[11px] text-zinc-600">
          <p data-testid="pipeline-worker-counts">
            {tr(
              `${etat.queue_size} en attente · ${etat.active_count} en cours`,
              `${etat.queue_size} waiting · ${etat.active_count} running`,
            )}
          </p>
          {/* Tâche 1440 puis 1476 (Eric) — Estimation calculée sur les
              traitements déjà faits : par minute de vidéo quand la durée est
              connue, à défaut par Mo de fichier (approximatif, et annoncé
              comme tel). On dit aussi dans combien de temps la file ENTIÈRE
              sera vidée — c'est la question qu'on se pose devant la carte de
              l'ouvrier. Sans historique, on le DIT : un chiffre faux affirmé
              avec aplomb est pire que pas de chiffre (acquis 1400). */}
          {etat.attentes && (
            etat.attentes.debit?.estimable ? (
              <p className="mt-1 text-zinc-700" data-testid="pipeline-worker-eta">
                {(() => {
                  const fins = Object.values(etat.attentes.estimations || {})
                    .map((e) => e.minutes_restantes).filter((m) => m != null);
                  const dernier = fins.length ? Math.round(Math.max(...fins)) : null;
                  const base = etat.attentes.debit.suffisant
                    ? tr(
                      `${etat.attentes.debit.secondes_par_minute} s de traitement par minute de vidéo (médiane des ${etat.attentes.debit.echantillon} derniers traitements, ${etat.attentes.ouvriers} ouvrier(s))`,
                      `${etat.attentes.debit.secondes_par_minute} s of processing per minute of video (median of the last ${etat.attentes.debit.echantillon} jobs, ${etat.attentes.ouvriers} worker(s))`,
                    )
                    : tr(
                      `${etat.attentes.debit.secondes_par_mo} s de traitement par Mo de fichier — estimation approximative, faute de durée connue (médiane des ${etat.attentes.debit.echantillon_mo} derniers traitements, ${etat.attentes.ouvriers} ouvrier(s))`,
                      `${etat.attentes.debit.secondes_par_mo} s of processing per MB of file — approximate estimate, no known duration (median of the last ${etat.attentes.debit.echantillon_mo} jobs, ${etat.attentes.ouvriers} worker(s))`,
                    );
                  return dernier == null
                    ? base
                    : tr(`File entièrement traitée dans ~${dernier} min · ${base}`,
                         `Queue fully processed in ~${dernier} min · ${base}`);
                })()}
              </p>
            ) : (
              <p className="mt-1 text-zinc-500" data-testid="pipeline-worker-eta-insuffisant">
                {tr(
                  `Attente non estimable : seulement ${etat.attentes.debit?.echantillon ?? 0} traitement(s) mesurable(s) dans l'historique. Aucune durée n'est inventée.`,
                  `Wait cannot be estimated: only ${etat.attentes.debit?.echantillon ?? 0} measurable job(s) in the history. No duration is invented.`,
                )}
              </p>
            )
          )}
          {/* L'avertissement mémoire est demandé par Eric, et il est précis :
              un chiffre sans conséquence nommée serait monté au maximum « pour
              voir », et l'OOM-killer ferait repartir tout le serveur en
              boucle (incident de production 2026-05-02). */}
          <p className="mt-1 text-amber-800" data-testid="pipeline-worker-warning">
            {tr(
              `Maximum ${etat.max_allowed}. Chaque traitement simultané ajoute ffmpeg, Whisper et la synthèse en mémoire : au-delà de ce que le serveur supporte, il est tué et redémarre en boucle.`,
              `Maximum ${etat.max_allowed}. Each parallel job adds ffmpeg, Whisper and the synthesis in memory: beyond what the server can hold, it gets killed and restarts in a loop.`,
            )}
          </p>
          {!isFounder && (
            <p className="mt-1 text-zinc-500" data-testid="pipeline-worker-readonly">
              {tr("Réglage réservé aux fondateurs.", "Founders only.")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
